import React, { useState, useRef, useEffect, useCallback } from "react";
import { Scissors } from "lucide-react";
import Logo from "./Logo";
import useObjectUrl from "../hooks/useObjectUrl";
import { splitVideo, downloadBlob } from "../services/ffmpeg";
import "./FileSplitter.css";

// Target size of each segment in MB.
const CHUNK_SIZE_MB = 800;

const FileSplitter = ({ onBackToMain, preloadedFile = null }) => {
  const [videoFile, setVideoFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [videoDuration, setVideoDuration] = useState(0);
  const [logMessages, setLogMessages] = useState([]);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);

  const videoPreview = useObjectUrl(videoFile);

  const handleFile = useCallback((file) => {
    setError("");
    setProgress(0);
    setLogMessages([]);
    setVideoDuration(0);
    setVideoFile(file);
  }, []);

  useEffect(() => {
    if (preloadedFile) handleFile(preloadedFile);
  }, [preloadedFile, handleFile]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFile(file);
  };

  const handleVideoMetadata = () => {
    if (videoRef.current) setVideoDuration(videoRef.current.duration);
  };

  const fileSizeInMB = videoFile ? videoFile.size / (1024 * 1024) : 0;
  const segmentCount = videoFile ? Math.ceil(fileSizeInMB / CHUNK_SIZE_MB) : 0;
  const segmentSeconds =
    videoFile && videoDuration ? (videoDuration * CHUNK_SIZE_MB) / fileSizeInMB : 0;

  const addLogMessage = (message) => setLogMessages((prev) => [...prev, message]);

  const handleSplit = async () => {
    if (!videoFile || !videoDuration) return;

    setIsProcessing(true);
    setProgress(0);
    setError("");
    setLogMessages([`Segment size set to ${CHUNK_SIZE_MB}MB`]);

    const baseName = videoFile.name.replace(/\.[^/.]+$/, "");

    try {
      const count = await splitVideo({
        file: videoFile,
        segmentSeconds: Math.max(1, Math.round(segmentSeconds)),
        onProgress: (fraction) => setProgress(fraction * 100),
        onLog: addLogMessage,
        onSegment: ({ index, blob }) => downloadBlob(blob, `${baseName}_part${index + 1}.mp4`),
      });
      addLogMessage(`Successfully created ${count} segments`);
      addLogMessage("Splitting complete!");
      setProgress(100);
    } catch (err) {
      console.error("Error splitting video:", err);
      setError(`An error occurred: ${err.message}`);
      addLogMessage(`Error: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTriggerFileInput = () => fileInputRef.current?.click();

  return (
    <div className="file-splitter">
      <div className="splitter-header">
        <Logo />
      </div>

      {!videoFile ? (
        <div className="upload-container">
          <input
            type="file"
            ref={fileInputRef}
            accept="video/*"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          <div className="upload-box" onClick={handleTriggerFileInput}>
            <Scissors size={40} />
            <p>Upload video to split</p>
          </div>
          <div className="splitter-info">
            <p>This tool helps you split large videos into smaller segments.</p>
            <p>Each segment will maintain the original quality.</p>
            <p>Files will be split into segments of {CHUNK_SIZE_MB}MB each.</p>
          </div>
        </div>
      ) : (
        <div className="splitting-container">
          {videoPreview && (
            <div className="video-preview-container">
              <video
                ref={videoRef}
                src={videoPreview}
                className="video-preview"
                controls
                playsInline
                onLoadedMetadata={handleVideoMetadata}
              />

              <div className="video-info">
                <p><strong>File:</strong> {videoFile.name}</p>
                <p><strong>Size:</strong> {fileSizeInMB.toFixed(2)} MB</p>
                {videoDuration > 0 && (
                  <p>
                    <strong>Duration:</strong> {Math.floor(videoDuration / 60)}:
                    {Math.floor(videoDuration % 60).toString().padStart(2, "0")}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="split-controls">
            {videoDuration > 0 && (
              <div className="segment-info">
                <p>Will split into <strong>{segmentCount}</strong> segments of {CHUNK_SIZE_MB}MB each</p>
                <p>Each segment will be about <strong>{segmentSeconds.toFixed(1)}</strong> seconds long</p>
              </div>
            )}

            <button
              className="split-button"
              onClick={handleSplit}
              disabled={isProcessing || !videoDuration}
            >
              {isProcessing ? (
                <span className="processing-text">Processing...</span>
              ) : (
                <>
                  <Scissors size={24} />
                  <span>Split Video</span>
                </>
              )}
            </button>

            {!videoDuration && (
              <div className="video-loading">
                <p>Loading video metadata...</p>
              </div>
            )}

            {onBackToMain && (
              <button className="splitter-back-button" onClick={onBackToMain} disabled={isProcessing}>
                Back to Highlighter
              </button>
            )}
          </div>

          {isProcessing && (
            <div className="progress-container">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <div className="progress-percentage">{Math.round(progress)}%</div>
            </div>
          )}

          {logMessages.length > 0 && (
            <div className="log-container">
              <div className="log-header">
                <h3>Processing Log</h3>
                {isProcessing && <div className="log-spinner"></div>}
              </div>
              <div className="log-messages">
                {logMessages.map((msg, index) => (
                  <div key={index} className="log-message">
                    {msg}
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}
        </div>
      )}
    </div>
  );
};

export default FileSplitter;
